import { ERRORS, type V1DraftRequest, type V1DraftResponse } from '@aira/shared';
import { useCallback, useRef, useState } from 'react';
import {
  createDraftRefinementOperationId,
  getAccessToken,
  type ProgressGroup,
  type ProgressStep,
  pollDraftRefinement,
  pollDraftRun,
  refineDraftRun,
  startDraftRun,
  streamDraftProgress,
} from '../api/ragBackend';

/**
 * Live progress via SSE, falling back to 1s polling if the stream can't be
 * established or drops mid-run. Both resolve to the terminal V1 response, so
 * callers don't care which path ran.
 */
async function runWithProgress(
  runId: string,
  bearer: string,
  onProgress: (steps: ProgressStep[]) => void,
  onGroupedProgress: (groups: ProgressGroup[]) => void,
): Promise<V1DraftResponse> {
  try {
    return await streamDraftProgress(runId, bearer, onProgress, onGroupedProgress);
  } catch {
    return pollDraftRun(runId, bearer, onProgress, onGroupedProgress);
  }
}

export type DraftPhase =
  | 'idle'
  | 'loading'
  | 'success'
  | 'missing_entity'
  | 'medium_confidence'
  | 'low_confidence'
  | 'out_of_scope'
  | 'tool_failure'
  | 'error';

export type DraftState =
  | { phase: 'idle' | 'loading' }
  | {
      phase:
        | 'success'
        | 'missing_entity'
        | 'medium_confidence'
        | 'low_confidence'
        | 'out_of_scope'
        | 'tool_failure';
      response: V1DraftResponse;
    }
  | { phase: 'error'; message: string };

export function responseToPhase(
  r: V1DraftResponse,
): Exclude<DraftPhase, 'idle' | 'loading' | 'error'> {
  if (r.status === 'missing_entity') return 'missing_entity';
  if (r.status === 'out_of_scope') return 'out_of_scope';
  if (r.status === 'tool_failure') return 'tool_failure';
  if (r.status === 'low_confidence' || r.badge === 'low') return 'low_confidence';
  if (r.badge === 'medium') return 'medium_confidence';
  return 'success';
}

export function inputFrom(req: V1DraftRequest, extra?: string) {
  return {
    emailBody: req.emailBody,
    emailSubject: req.emailSubject,
    senderEmail: req.senderEmail,
    senderName: req.senderName,
    emailMessageId: req.emailMessageId,
    emailReceivedAt: req.emailReceivedAt,
    forceNew: req.forceNew,
    extraInstructions: [req.extraInstructions, extra].filter(Boolean).join(' ') || undefined,
  };
}

/**
 * Drives a draft against the new aira-rag-backend (see ../api/ragBackend).
 * The backend has a dedicated same-run refine endpoint, so:
 *   - retry      → re-kick a run with the resolved entity in the instruction
 *   - refine     → update the completed run using its current edited draft
 *   - hydrate    → replaces state with a response generated elsewhere
 *                  (e.g. the Office Dialog popup posted it via messageParent)
 */
export function useDraftRequest() {
  const [state, setState] = useState<DraftState>({ phase: 'idle' });
  // Real backend phases for the thinking panel (no hardcoded steps).
  const [progress, setProgress] = useState<ProgressStep[]>([]);
  const [groupedProgress, setGroupedProgress] = useState<ProgressGroup[]>([]);
  // Run id of the in-flight backend draft. Exposed so the Office Dialog popup
  // can attach to the same run instead of starting a duplicate one.
  const [currentRunId, setCurrentRunId] = useState<string | null>(null);
  const [currentRefinementOperationId, setCurrentRefinementOperationId] = useState<string | null>(
    null,
  );
  const [isRefining, setIsRefining] = useState(false);
  const [refineError, setRefineError] = useState<string | null>(null);
  const regenerateCount = useRef(0);
  const lastReq = useRef<V1DraftRequest | null>(null);
  const refiningRunIdRef = useRef<string | null>(null);
  // Bumped on every new run AND on reset(). A run captures the token at start
  // and drops its result if the token changed meanwhile — so a Back/Cancel
  // during the thinking phase truly aborts instead of being overwritten when
  // the in-flight poll resolves later.
  const runToken = useRef(0);

  const drive = useCallback(
    async (input: {
      emailBody: string;
      emailSubject?: string;
      senderEmail?: string;
      senderName?: string;
      emailMessageId?: string;
      emailReceivedAt?: string;
      forceNew?: boolean;
      extraInstructions?: string;
    }) => {
      const token = ++runToken.current;
      refiningRunIdRef.current = null;
      setIsRefining(false);
      setRefineError(null);
      setProgress([]);
      setGroupedProgress([]);
      setCurrentRunId(null);
      setCurrentRefinementOperationId(null);
      setState({ phase: 'loading' });
      try {
        const { runId, token: bearer } = await startDraftRun(input);
        if (runToken.current !== token) return;
        setCurrentRunId(runId);
        const data = await runWithProgress(
          runId,
          bearer,
          (s) => {
            if (runToken.current === token) setProgress(s);
          },
          (g) => {
            if (runToken.current === token) setGroupedProgress(g);
          },
        );
        if (runToken.current !== token) return;
        setCurrentRunId(null);
        setState({ phase: responseToPhase(data), response: data });
      } catch (err) {
        if (runToken.current === token) {
          setCurrentRunId(null);
          setState({ phase: 'error', message: String(err) });
        }
      }
    },
    [],
  );

  const request = useCallback(
    async (req: V1DraftRequest) => {
      regenerateCount.current = 0;
      lastReq.current = req;
      await drive(inputFrom(req));
    },
    [drive],
  );

  const retry = useCallback(
    async (entityKey: string, entityValue: string, origReq: V1DraftRequest) => {
      lastReq.current = origReq;
      await drive(inputFrom(origReq, `The customer's ${entityKey} is ${entityValue}.`));
    },
    [drive],
  );

  const refine = useCallback(
    async (
      notes: string | undefined,
      response: V1DraftResponse,
      currentDraft: string,
    ): Promise<V1DraftResponse | null> => {
      if (refiningRunIdRef.current === response.trace_id) return null;
      if (regenerateCount.current >= 3) {
        setRefineError(ERRORS.regenerateLimitReached);
        return null;
      }
      refiningRunIdRef.current = response.trace_id;
      setIsRefining(true);
      regenerateCount.current += 1;
      const note = notes?.trim() || 'Improve the draft.';
      const operationId = createDraftRefinementOperationId();
      const token = ++runToken.current;
      setCurrentRunId(response.trace_id);
      setCurrentRefinementOperationId(operationId);
      setRefineError(null);
      try {
        const data = await refineDraftRun(response.trace_id, note, currentDraft, operationId);
        if (runToken.current !== token) return null;
        setCurrentRunId(null);
        setCurrentRefinementOperationId(null);
        setRefineError(null);
        setState({ phase: responseToPhase(data), response: data });
        return data;
      } catch (err) {
        if (runToken.current === token) {
          setCurrentRunId(null);
          setCurrentRefinementOperationId(null);
          setRefineError(String(err));
          const preservedResponse = { ...response, draft: currentDraft };
          setState({ phase: responseToPhase(preservedResponse), response: preservedResponse });
        }
        throw err;
      } finally {
        // A dialog handoff bumps the token. Only the poll that still owns the
        // hook may clear refinement state; a stale promise must leave it alone.
        if (runToken.current === token && refiningRunIdRef.current === response.trace_id) {
          refiningRunIdRef.current = null;
          setIsRefining(false);
        }
      }
    },
    [],
  );

  /**
   * Attach to an existing backend run (e.g. one the taskpane started before
   * the dialog re-claimed it). Bumps runToken so any still-running poll from
   * a previous call drops its result. Used by the dialog handoff cancel path.
   */
  const attachToRun = useCallback(
    async (
      runId: string,
      initialProgress?: {
        progress?: ProgressStep[];
        groupedProgress?: ProgressGroup[];
        regenerateCount?: number;
        operationId?: string;
        preservedResponse?: V1DraftResponse;
      },
    ) => {
      const token = ++runToken.current;
      refiningRunIdRef.current = initialProgress?.operationId ? runId : null;
      if (typeof initialProgress?.regenerateCount === 'number') {
        regenerateCount.current = initialProgress.regenerateCount;
      }
      setIsRefining(Boolean(initialProgress?.operationId));
      setProgress(initialProgress?.progress ?? []);
      setGroupedProgress(initialProgress?.groupedProgress ?? []);
      setCurrentRunId(runId);
      setCurrentRefinementOperationId(initialProgress?.operationId ?? null);
      setRefineError(null);
      if (initialProgress?.operationId && initialProgress.preservedResponse) {
        setState({
          phase: responseToPhase(initialProgress.preservedResponse),
          response: initialProgress.preservedResponse,
        });
      } else {
        setState({ phase: 'loading' });
      }
      try {
        const bearer = await getAccessToken();
        const data = initialProgress?.operationId
          ? await pollDraftRefinement(runId, bearer, initialProgress.operationId)
          : await pollDraftRun(
              runId,
              bearer,
              (s) => {
                if (runToken.current === token) setProgress(s);
              },
              (g) => {
                if (runToken.current === token) setGroupedProgress(g);
              },
            );
        if (runToken.current !== token) return;
        setCurrentRunId(null);
        setCurrentRefinementOperationId(null);
        setState({ phase: responseToPhase(data), response: data });
      } catch (err) {
        if (runToken.current === token) {
          setCurrentRunId(null);
          setCurrentRefinementOperationId(null);
          if (initialProgress?.operationId && initialProgress.preservedResponse) {
            setRefineError(String(err));
            setState({
              phase: responseToPhase(initialProgress.preservedResponse),
              response: initialProgress.preservedResponse,
            });
          } else {
            setState({ phase: 'error', message: String(err) });
          }
        }
      } finally {
        if (runToken.current === token) {
          if (refiningRunIdRef.current === runId) refiningRunIdRef.current = null;
          setIsRefining(false);
        }
      }
    },
    [],
  );

  /**
   * Replaces the current state with a draft response that came from elsewhere
   * (e.g. the Office Dialog popup posted it back via messageParent). Resets
   * any in-flight run token so a still-polling request from the taskpane
   * cannot stomp the hydrated state.
   */
  const hydrate = useCallback(
    (
      response: V1DraftResponse,
      nextProgress?: ProgressStep[],
      nextGroups?: ProgressGroup[],
      nextRegenerateCount?: number,
      nextRefineError?: string | null,
    ) => {
      runToken.current++;
      if (typeof nextRegenerateCount === 'number') {
        regenerateCount.current = nextRegenerateCount;
      }
      setProgress(nextProgress ?? []);
      setGroupedProgress(nextGroups ?? []);
      setCurrentRunId(null);
      setCurrentRefinementOperationId(null);
      refiningRunIdRef.current = null;
      setIsRefining(false);
      setRefineError(nextRefineError ?? null);
      setState({ phase: responseToPhase(response), response });
    },
    [],
  );

  /**
   * Stop the in-flight poll loop without changing state. Used when the dialog
   * takes over the run — the taskpane should not keep racing the dialog for
   * the same /events endpoint.
   */
  const suspend = useCallback(() => {
    runToken.current++;
  }, []);

  const reset = useCallback(() => {
    regenerateCount.current = 0;
    lastReq.current = null;
    // Invalidate any in-flight run so a Back/Cancel during the thinking phase
    // doesn't get overwritten when that run eventually resolves.
    runToken.current++;
    setProgress([]);
    setGroupedProgress([]);
    setCurrentRunId(null);
    setCurrentRefinementOperationId(null);
    refiningRunIdRef.current = null;
    setIsRefining(false);
    setRefineError(null);
    setState({ phase: 'idle' });
  }, []);

  return {
    state,
    progress,
    groupedProgress,
    currentRunId,
    currentRefinementOperationId,
    isRefining,
    refineError,
    request,
    retry,
    refine,
    hydrate,
    attachToRun,
    suspend,
    reset,
    regenerateCount,
  };
}
